import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { alpha, Box, Collapse, Grid, type SxProps, type Theme, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, type NavLinkProps, useLocation, useNavigate } from 'react-router-dom';
import { styles } from '@/components/StyledComponents';
import { Popover } from '@/components/ui/popover';
import { paths } from '@/constants';
import { useColorMode } from '@/hooks';
import { usePrefValue } from '@/preferences';

interface child {
    title: string;
    to: string;
    disabled?: boolean;
    onClick?: () => void;
    specialCondition?: boolean;
    matchStartsWith?: boolean;
}
interface Props {
    to: NavLinkProps['to'];
    link: {
        component: React.FC;
        title: string;
        textColor?: (theme: Theme) => string;
        iconSize?: string;
        svg?: React.ReactNode;
    };
    uppercase?: boolean;
    fontWeight?: string;
    fontSize?: string;
    mt?: string;
    mr?: string;
    testId?: string;
    onClick?: () => void;
    sxTitle?: (theme: Theme) => SxProps<Theme>;
    marginLeftText?: string;
    readonly?: boolean;
    disabled?: boolean;
    matchStartsWith?: boolean;
    children?: child[];
    isOpen?: boolean;
    setOpen?: (state: string) => void;
}

export const NavigationLink = (props: Props) => {
    const side_bar = usePrefValue('side_bar');
    const episode_plan_menu = usePrefValue('episode_plan_menu');
    const location = useLocation();
    const [isActive, setIsActive] = useState(false);
    const isDarkMode = useColorMode();
    const navigate = useNavigate();
    const {
        onClick,
        uppercase = false,
        fontWeight = '400',
        fontSize = '0.875rem',
        mt = '0',
        mr = '5px',
        to,
        link,
        testId,
        sxTitle = {},
        marginLeftText,
        readonly,
        disabled,
        matchStartsWith,
        children,
        isOpen = false,
        setOpen,
    } = props;

    const [isUnderHover, setUnderHover] = useState(-1);
    const [isHover, setHover] = useState(false);

    const isPathMatch = useCallback((childItem: child, pathname: string) => {
        if (childItem.matchStartsWith) {
            // Ensure exact segment match: path must equal or be followed by '/'
            return pathname === childItem.to || pathname.startsWith(`${childItem.to}/`);
        }
        return pathname === childItem.to;
    }, []);
    const anchorRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const isOverPopover = useRef(false);
    const isOverTrigger = useRef(false);

    const handlePopoverOpen = () => {
        isOverTrigger.current = true;
        setHover(true);
    };

    const handlePopoverClose = () => {
        isOverTrigger.current = false;
        setTimeout(() => {
            if (!isOverPopover.current && !isOverTrigger.current) {
                setHover(false);
            }
        }, 50);
    };

    const handlePopoverContentEnter = () => {
        isOverPopover.current = true;
    };

    const handlePopoverContentLeave = () => {
        isOverPopover.current = false;
        setTimeout(() => {
            if (!isOverPopover.current && !isOverTrigger.current) {
                setHover(false);
            }
        }, 50);
    };

    useEffect(() => {
        if (to === paths.project.lines.index || to === paths.project.episodes.index) {
            switch (to) {
                case paths.project.lines.index:
                    if (location.pathname.slice(0, 13) === paths.project.line.index || to === location.pathname) {
                        setIsActive(true);
                    } else {
                        setIsActive(false);
                    }
                    break;
                case paths.project.episodes.index:
                    if (
                        (episode_plan_menu === '2' && location.pathname.slice(0, 16) === paths.project.episode.index && location.search) ||
                        to === location.pathname
                    ) {
                        setIsActive(true);
                    } else {
                        setIsActive(false);
                    }
                    break;
            }
        } else {
            // Check for exact match or prefix match if matchStartsWith is enabled
            const toStr = typeof to === 'string' ? to : '';
            const currentFullPath = location.pathname + location.search;
            const isExactMatch = location.pathname === to || currentFullPath === to;
            const isPrefixMatch = matchStartsWith && toStr && (location.pathname === toStr || location.pathname.startsWith(`${toStr}/`));

            if (isExactMatch || isPrefixMatch) {
                setIsActive(true);
            } else {
                setIsActive(false);
                if (children) {
                    children.forEach(el => {
                        if (isPathMatch(el, location.pathname)) {
                            if (el.to === paths.project.episode.index && episode_plan_menu !== '1') {
                                setIsActive(false);
                            } else {
                                setIsActive(true);
                            }
                        }
                    });
                } else {
                    setIsActive(false);
                }
            }
        }
    }, [location, episode_plan_menu, matchStartsWith, to, isPathMatch, children]);

    const CompactHover = () => (
        <Box
            ref={popoverRef}
            onMouseEnter={handlePopoverContentEnter}
            onMouseLeave={handlePopoverContentLeave}
            sx={{
                borderRadius: '0 8px 8px 0',
                overflow: 'hidden',
            }}
        >
            <NavLink
                to={readonly || children ? location : to}
                style={{
                    fontSize,
                    fontWeight,
                    textDecoration: 'none',
                }}
                title={link.title}
                onClick={() => {
                    if (!children) {
                        setHover(false);
                    }
                }}
            >
                <Box
                    data-testid={testId}
                    onClick={e => {
                        if (onClick) {
                            e.stopPropagation();
                            onClick();
                        }
                        setOpen?.(isOpen ? '' : link.title);
                    }}
                    sx={[
                        {
                            fontSize,
                            cursor: 'pointer',
                            color: 'primary.light',
                            textTransform: uppercase ? 'uppercase' : 'none',
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            marginTop: mt,
                            position: 'relative',
                            padding: '9.5px 8px',
                            borderRadius: children ? '0 8px 0 0' : '0 8px 8px 0',
                            '& svg': {
                                fill: theme => (isDarkMode ? theme.custom.c1 : theme.custom.c22),
                            },
                        },
                        {
                            background: theme => (isDarkMode ? theme.palette.background.paper : theme.custom.c7),
                            '& svg': {
                                color: theme => theme.palette.secondary.main,
                                fill: theme => theme.palette.secondary.main,
                            },
                        },
                    ]}
                >
                    <Box
                        sx={[
                            {
                                color: theme => (link?.textColor ? link.textColor(theme) : theme.palette.text.secondary),
                            },
                            sxTitle,
                            {
                                color: theme => theme.custom.c22,
                            },
                            isActive && { color: theme => theme.palette.secondary.main },
                        ]}
                    >
                        {link.title}
                    </Box>
                </Box>
            </NavLink>
            {children && (
                <Grid
                    container
                    sx={{
                        width: 'auto',
                        overflow: 'hidden',
                        background: theme => theme.palette.background.paper,
                        paddingLeft: '6px',
                        borderRadius: '0 0 8px 0',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
                    }}
                >
                    {children.map((el, index) => (
                        <NavLink
                            key={el.title}
                            title={el.title}
                            to={el.disabled ? location.pathname : el.to}
                            style={{
                                textDecoration: 'none',
                                minHeight: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                            }}
                            onClick={() => {
                                setHover(false);
                                if (el.onClick) {
                                    el.onClick();
                                }
                            }}
                        >
                            <Grid
                                container
                                gap="26px"
                                alignItems="center"
                                sx={[
                                    { padding: '8px', borderRadius: '8px', flexWrap: 'nowrap' },
                                    {
                                        '& svg': {
                                            color: theme =>
                                                el.disabled
                                                    ? theme.palette.secondary.contrastText
                                                    : (isPathMatch(el, location.pathname) && el.specialCondition) || isUnderHover === index
                                                      ? theme.palette.secondary.main
                                                      : theme.custom.c22,
                                            fontSize: '0.5rem',
                                        },
                                    },
                                    {
                                        '&:hover': {
                                            background: theme =>
                                                isDarkMode ? alpha(theme.palette.secondary.light, 0.04) : alpha(theme.custom.c22, 0.05),
                                        },
                                    },
                                    isPathMatch(el, location.pathname) &&
                                        Boolean(el.specialCondition) && {
                                            background: theme =>
                                                isDarkMode ? alpha(theme.palette.secondary.light, 0.04) : alpha(theme.custom.c22, 0.05),
                                        },
                                ]}
                            >
                                <FiberManualRecordIcon />
                                <Typography
                                    children={el.title}
                                    sx={{
                                        color: theme =>
                                            isUnderHover === index
                                                ? theme.custom.c22
                                                : isPathMatch(el, location.pathname) && el.specialCondition
                                                  ? theme.palette.secondary.main
                                                  : el.disabled
                                                    ? theme.palette.secondary.contrastText
                                                    : isDarkMode
                                                      ? theme.palette.primary.contrastText
                                                      : theme.custom.c22,
                                    }}
                                    variant="body2"
                                />
                            </Grid>
                        </NavLink>
                    ))}
                </Grid>
            )}
        </Box>
    );

    return (
        <>
            <div
                ref={anchorRef}
                style={{
                    fontSize,
                    fontWeight,
                    textDecoration: 'none',
                    width: side_bar ? '100%' : '40px',
                }}
                title={link.title}
                onMouseEnter={() => {
                    if (side_bar) {
                        setHover(true);
                    } else {
                        handlePopoverOpen();
                    }
                }}
                onMouseLeave={() => {
                    if (side_bar) {
                        setHover(false);
                    } else {
                        handlePopoverClose();
                    }
                }}
            >
                <Box
                    data-testid={testId}
                    onClick={_e => {
                        if (onClick) {
                            onClick();
                        }
                        if (!readonly && !disabled && !children) {
                            navigate(to);
                        }
                        setOpen?.(isOpen ? '' : link.title);
                    }}
                    sx={[
                        {
                            fontSize,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            color: 'primary.light',
                            textTransform: uppercase ? 'uppercase' : 'none',
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            marginTop: mt,
                            position: 'relative',
                            padding: '10px 8px',
                            borderRadius: isHover && !side_bar ? '8px 0 0 8px' : '8px',
                            '& svg': {
                                fill: theme => (isDarkMode ? theme.custom.c1 : theme.custom.c22),
                            },
                        },
                        !disabled &&
                            (isOpen || isActive || isHover) && {
                                background: theme =>
                                    isDarkMode
                                        ? side_bar
                                            ? alpha(theme.palette.secondary.light, 0.04)
                                            : theme.palette.background.paper
                                        : side_bar
                                          ? alpha(theme.palette.secondary.main, 0.1)
                                          : theme.custom.c7,
                                '& svg': {
                                    color: theme => theme.palette.secondary.main,
                                    fill: theme => theme.palette.secondary.main,
                                },
                            },
                        sxTitle,
                        !disabled &&
                            isHover && {
                                color: theme => theme.custom.c22,
                            },
                        !disabled && !isHover && !isDarkMode && { color: theme => theme.custom.c22 },
                        !disabled && isActive && { color: theme => theme.palette.secondary.main },
                    ]}
                >
                    <Box
                        className="icon"
                        sx={[
                            styles.iconPapper,
                            {
                                marginRight: mr,
                                background: 'none',
                                '& svg': {
                                    color: theme =>
                                        readonly || disabled
                                            ? theme.palette.secondary.contrastText
                                            : isActive
                                              ? theme.palette.secondary.main
                                              : theme.palette.primary.main,
                                    fontSize: link.iconSize ? link.iconSize : 'auto',
                                },
                            },
                        ]}
                    >
                        {link.svg ? link.svg : <link.component />}
                    </Box>

                    {side_bar && (
                        <Box
                            sx={[
                                {
                                    marginLeft: marginLeftText || '16px',
                                    color: theme =>
                                        disabled
                                            ? theme.palette.secondary.contrastText
                                            : isActive || isOpen
                                              ? theme.palette.secondary.main
                                              : isHover
                                                ? theme.custom.c22
                                                : !isDarkMode
                                                  ? theme.custom.c22
                                                  : link?.textColor
                                                    ? link.textColor(theme)
                                                    : theme.palette.text.secondary,
                                },
                                sxTitle,
                            ]}
                        >
                            {link.title}
                        </Box>
                    )}
                    {children && (
                        <ArrowRightIcon
                            sx={[
                                {
                                    transition: '0.8s',
                                    position: 'absolute',
                                    color: theme => theme.palette.primary.contrastText,
                                    height: '1rem',
                                },
                                isOpen && side_bar && { transform: 'rotate(90deg)' },
                                side_bar ? { right: 0 } : { right: '-4px' },
                            ]}
                        />
                    )}
                </Box>
                {children && side_bar && (
                    <Collapse in={isOpen} mountOnEnter>
                        <Grid
                            container
                            sx={[
                                {
                                    width: 'auto',
                                    transition: '0.8s',
                                    overflow: 'hidden',
                                    background: theme => theme.palette.background.paper,
                                    marginLeft: '6px',
                                    borderBottomRightRadius: '8px',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                },
                                isOpen &&
                                    !side_bar && {
                                        background: theme => theme.palette.background.paper,
                                        borderTopRightRadius: 0,
                                    },
                            ]}
                        >
                            {children?.map((el, index) => (
                                <NavLink
                                    key={el.title}
                                    title={el.title}
                                    to={el.disabled ? location.pathname : el.to}
                                    style={{
                                        textDecoration: 'none',
                                        minHeight: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <Grid
                                        container
                                        gap="26px"
                                        alignItems="center"
                                        onMouseEnter={() => setUnderHover(index)}
                                        onMouseLeave={() => setUnderHover(-1)}
                                        onClick={_e => {
                                            if (el.onClick) {
                                                el.onClick();
                                            }
                                        }}
                                        sx={[
                                            {
                                                padding: '8px',
                                                borderRadius: '8px',
                                                flexWrap: 'nowrap',
                                            },
                                            {
                                                '& svg': {
                                                    color: theme =>
                                                        el.disabled
                                                            ? theme.palette.secondary.contrastText
                                                            : (isPathMatch(el, location.pathname) && el.specialCondition) ||
                                                                isUnderHover === index
                                                              ? theme.palette.secondary.main
                                                              : theme.custom.c22,
                                                    fontSize: '0.5rem',
                                                },
                                            },
                                            ((isPathMatch(el, location.pathname) && el.specialCondition) || isUnderHover === index) && {
                                                background: theme =>
                                                    isDarkMode ? alpha(theme.palette.secondary.light, 0.04) : theme.custom.c7,
                                            },
                                        ]}
                                    >
                                        <FiberManualRecordIcon />
                                        <Typography
                                            children={el.title}
                                            sx={{
                                                color: theme =>
                                                    isUnderHover === index
                                                        ? theme.custom.c22
                                                        : isPathMatch(el, location.pathname) && el.specialCondition
                                                          ? theme.palette.secondary.main
                                                          : el.disabled
                                                            ? theme.palette.secondary.contrastText
                                                            : isDarkMode
                                                              ? theme.palette.primary.contrastText
                                                              : theme.custom.c22,
                                            }}
                                            variant="body2"
                                        />
                                    </Grid>
                                </NavLink>
                            ))}
                        </Grid>
                    </Collapse>
                )}
            </div>
            {!side_bar && (
                <Popover
                    open={isHover}
                    anchorEl={anchorRef.current}
                    onClose={() => setHover(false)}
                    disableRestoreFocus
                    side="right"
                    align="start"
                    modal={false}
                    className="bg-transparent shadow-none overflow-visible"
                    onMouseEnter={handlePopoverContentEnter}
                    onMouseLeave={handlePopoverContentLeave}
                >
                    <CompactHover />
                </Popover>
            )}
        </>
    );
};
